import { CONDITION_RATINGS } from '../../../../lib/assetCreation';

function SourceTag({ tag }) {
  const cls = `add-asset-tag${tag === 'GROUP' ? ' add-asset-tag--group' : ''}`;
  return <span className={cls}>{tag}</span>;
}

/**
 * The PRE-FILLED section of the mobile Add Asset form: every row the tech should
 * not normally have to touch, each tagged with where its value came from.
 *
 * Purely presentational — it takes values and change handlers. `locationError`
 * state stays with the caller because the save guard is what sets it; this only
 * renders the error decoration.
 *
 * Props:
 *   siteName, elementType — locked session context, shown as static rows
 *   formData — the draft being edited
 *   set — (field) => onChange handler factory from the caller
 *   handleAssetTypeChange — asset-type select handler (recomputes dependent fields)
 *   assetTypeOptions — options for the asset-type select
 *   showDbNumberRow — show the inherited DB NUMBER row (RCDs only; a board sets
 *     its own DB number in the NEEDS INPUT block, so it must not appear here too)
 *   locationError — highlight Building/Room as required
 *   buildingRowRef — scroll target for the blocked-save guard
 */
export default function AddAssetPrefilledRows({
  siteName,
  elementType,
  formData,
  set,
  handleAssetTypeChange,
  assetTypeOptions = [],
  showDbNumberRow = false,
  locationError = false,
  buildingRowRef,
}) {
  return (
    <>
      <div className="add-asset-form__prefilled-head">
        <p className="add-asset-form__section-label">PRE-FILLED</p>
        <span className="add-asset-form__prefilled-hint">Tap any value to change</span>
      </div>

      <div className="add-asset-form__rows">
        <div className="add-asset-row">
          <span className="add-asset-row__label">SITE</span>
          <span className="add-asset-row__value add-asset-row__value--static">{siteName}</span>
          <SourceTag tag="GROUP" />
        </div>

        <div className="add-asset-row">
          <span className="add-asset-row__label">ELEMENT TYPE</span>
          <span className="add-asset-row__value add-asset-row__value--static">
            {elementType === 'fitments' ? 'Fitments' : 'Electrical'}
          </span>
          <SourceTag tag="GROUP" />
        </div>

        <div className="add-asset-row">
          <span className="add-asset-row__label">ASSET TYPE</span>
          <select
            className="add-asset-row__select"
            value={formData.assetType}
            onChange={handleAssetTypeChange}
          >
            {assetTypeOptions.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <SourceTag tag="GROUP" />
        </div>

        <div
          ref={buildingRowRef}
          className={`add-asset-row${locationError ? ' add-asset-row--error' : ''}`}
        >
          <span className="add-asset-row__label">BUILDING</span>
          <input
            className="add-asset-row__input"
            value={formData.building}
            onChange={set('building')}
            aria-invalid={locationError || undefined}
          />
          <SourceTag tag="GROUP" />
        </div>

        <div className={`add-asset-row${locationError ? ' add-asset-row--error' : ''}`}>
          <span className="add-asset-row__label">ROOM</span>
          <input
            className="add-asset-row__input"
            value={formData.room}
            onChange={set('room')}
            aria-invalid={locationError || undefined}
          />
          <SourceTag tag="GROUP" />
        </div>
        {locationError && (
          <p className="add-asset-row__error-msg" role="alert">
            Enter a Building or Room to save.
          </p>
        )}

        {showDbNumberRow && (
          <div className="add-asset-row">
            <span className="add-asset-row__label">DB NUMBER</span>
            <input className="add-asset-row__input" value={formData.dbNumber} onChange={set('dbNumber')} />
            <SourceTag tag="GROUP" />
          </div>
        )}

        <div className="add-asset-row">
          <span className="add-asset-row__label">MANUFACTURER</span>
          <input className="add-asset-row__input" value={formData.manufacturer} onChange={set('manufacturer')} />
          <SourceTag tag="LAST" />
        </div>

        <div className="add-asset-row">
          <span className="add-asset-row__label">MODEL</span>
          <input className="add-asset-row__input" value={formData.model} onChange={set('model')} />
          <SourceTag tag="LAST" />
        </div>

        {/* Serial number has no source to inherit from — it is unique per device, so
            it starts blank and carries no source tag. */}
        <div className="add-asset-row">
          <span className="add-asset-row__label">SERIAL NUMBER</span>
          <input
            className="add-asset-row__input"
            value={formData.serialNumber}
            onChange={set('serialNumber')}
            placeholder="Unique per device"
          />
        </div>

        <div className="add-asset-row">
          <span className="add-asset-row__label">INSTALLED</span>
          <input
            type="date"
            className="add-asset-row__input add-asset-row__input--date"
            value={formData.installationDate}
            onChange={set('installationDate')}
          />
          <SourceTag tag="TODAY" />
        </div>

        <div className="add-asset-row">
          <span className="add-asset-row__label">CONDITION</span>
          <select className="add-asset-row__select" value={formData.conditionRating} onChange={set('conditionRating')}>
            {CONDITION_RATINGS.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <SourceTag tag="DEFAULT" />
        </div>
      </div>
    </>
  );
}
